import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import { Paprika } from '../../styles/theme';
import { PantryItemView } from '../../redux/pantrySelectors';
import { FoodTile } from './FoodTile';
import { FreshnessPill } from './FreshnessPill';
import { addedLabel } from './addedLabel';
import { usePantryName } from './usePantryName';
import { pantryUnitName } from '../../utils/pantryDisplayName';

interface Props {
  view: PantryItemView;
  onPress: () => void;
  right?: React.ReactNode;
}

export function PantryItemRow({ view, onPress, right }: Props) {
  const { t, i18n } = useTranslation();
  const pantryName = usePantryName();

  // Amount and arrival, e.g. "2 · Ajouté aujourd'hui". The arrival date is what
  // separates two lots of the same food: one from today's delivery, one that has
  // been in the fridge a week.
  const meta = [
    view.item.quantity ? `${view.item.quantity}${view.item.unit ? ` ${pantryUnitName(view.item.unit, i18n.language, view.item.quantity)}` : ''}` : '',
    addedLabel(t, view.item.dateAdded, undefined, i18n.language),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Pressable onPress={onPress} style={styles.row}>
      <FoodTile category={view.item.category} name={view.item.name} size={38} />
      <View style={styles.content}>
        <Text style={styles.name} numberOfLines={1}>
          {pantryName(view.item)}
        </Text>
        <Text style={styles.meta} numberOfLines={1}>
          {meta}
        </Text>
      </View>
      {right !== undefined ? (
        right
      ) : (
        <FreshnessPill state={view.state} days={view.days} compact={true} />
      )}
      <Feather name="chevron-right" size={20} color={Paprika.textMuted} style={styles.chevron} />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.hairline,
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    color: Paprika.textPrimary,
    lineHeight: 20,
  },
  meta: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textMuted,
    marginTop: 2,
    lineHeight: 16,
  },
  chevron: {
    marginLeft: 8,
  },
});
